import { useCallback, useState } from 'react'

import type { Attachment, RegionShot } from 'features/bug-report/types'
import {
  processUploadedFiles,
  UploadProcessResult,
} from 'features/bug-report/utils/uploads'

export type UseBugReportAttachmentsReturn = {
  allAttachments: Attachment[]
  visibleAttachments: Attachment[]
  setAutoShot: (dataUrl: string) => void
  addRegionShot: (dataUrl: string) => void
  addUploads: (files: File[]) => Promise<UploadProcessResult>
  removeAttachment: (id: string) => void
  reset: () => void
}

export function useBugReportAttachments(): UseBugReportAttachmentsReturn {
  const [autoShot, setAutoShotState] = useState<Attachment | null>(null)
  const [regionShots, setRegionShots] = useState<RegionShot[]>([])
  const [uploads, setUploads] = useState<Attachment[]>([])

  const allAttachments: Attachment[] = []
  if (autoShot) allAttachments.push(autoShot)
  regionShots.forEach((shot, i) => {
    const num = i + 1
    allAttachments.push({
      id: shot.id,
      dataUrl: shot.dataUrl,
      filename: `screenshot-${num}.png`,
      contentType: 'image/png',
      label: `Screenshot #${num}`,
      removable: true,
    })
  })
  allAttachments.push(...uploads)
  const visibleAttachments = allAttachments.filter((a) => a.removable)

  const setAutoShot = useCallback((dataUrl: string): void => {
    setAutoShotState({
      id: 'auto',
      dataUrl,
      filename: 'current-page.png',
      contentType: 'image/png',
      label: 'Current page',
      removable: false,
    })
  }, [])

  const addRegionShot = useCallback((dataUrl: string): void => {
    setRegionShots((prev) => [...prev, { id: `region-${Date.now()}`, dataUrl }])
  }, [])

  const addUploads = useCallback(
    async (files: File[]): Promise<UploadProcessResult> => {
      const result = await processUploadedFiles(files)
      if (result.accepted.length > 0) {
        setUploads((prev) => [...prev, ...result.accepted])
      }
      return result
    },
    [],
  )

  const removeAttachment = useCallback((id: string): void => {
    if (id.startsWith('region-')) {
      setRegionShots((prev) => prev.filter((s) => s.id !== id))
      return
    }
    setUploads((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const reset = useCallback((): void => {
    setAutoShotState(null)
    setRegionShots([])
    setUploads([])
  }, [])

  return {
    allAttachments,
    visibleAttachments,
    setAutoShot,
    addRegionShot,
    addUploads,
    removeAttachment,
    reset,
  }
}
